import { useId, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { azureApi, accessRequestsApi, resourcesApi } from '../services/api';
import { Resource } from '../types';
import { Plus, Building2, RefreshCw, ExternalLink, Trash2, Shield, Clock } from 'lucide-react';
import { clsx } from 'clsx';
import { usePermissions } from '../hooks/usePermissions';
import AccessRequestForm from '../components/AccessRequestForm';
import { toast } from '@/lib/toast';
import { errorMessage } from '@/lib/http';
import { Link } from 'react-router-dom';
import { Banner, Breadcrumbs, Button, Card, EmptyState, IconButton, Input, Modal, PageHeader, Select, StatusBadge, Textarea, OutcomeBadge, MonoCell } from '@/components/ds-zta';
import { useDialogs } from '@/components/dialogs/DialogProvider';
import { BRAND } from '@/brand';
import ProviderMark from '@/components/ProviderMark';
import CardGridSkeleton from '@/components/CardGridSkeleton';

export default function AzureResources() {
  const [showSubForm, setShowSubForm] = useState(false);
  const [showAccessRequestForm, setShowAccessRequestForm] = useState(false);
  const [selectedResourceForRequest, setSelectedResourceForRequest] = useState<Resource | null>(null);
  const [showRoleMappings, setShowRoleMappings] = useState(false);
  const [showAddMapping, setShowAddMapping] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [pendingSub, setPendingSub] = useState<any>(null);
  const [azureAccessDescription, setAzureAccessDescription] = useState<string>('');
  const queryClient = useQueryClient();
  const { confirm } = useDialogs();
  const { isAdmin } = usePermissions();

  // Fetch subscriptions
  const { data: subsData, isLoading: subsLoading, isError: listError, refetch: refetchList } = useQuery('azure-subscriptions', () => azureApi.listSubscriptions());
  const subs = subsData?.data?.subscriptions || [];

  // Subscription CRUD
  const createSubMutation = useMutation((data: any) => azureApi.createSubscription(data), {
    onSuccess: () => { toast.success('Azure subscription added'); queryClient.invalidateQueries('azure-subscriptions'); setShowSubForm(false); },
    onError: (err) => { toast.error(errorMessage(err, 'Failed to add subscription')); },
  });

  const deleteSubMutation = useMutation((id: string) => azureApi.deleteSubscription(id), {
    onSuccess: () => { toast.success('Subscription removed'); queryClient.invalidateQueries('azure-subscriptions'); },
    onError: (err) => { toast.error(errorMessage(err, 'Failed to remove subscription')); },
  });

  // Access checks for non-admin users
  const { data: accessChecks } = useQuery(
    ['azure-sub-access', subs.map((s: any) => s.id).join(',')],
    async () => {
      const statusMap: Record<string, { hasAccess: boolean; resourceId?: string }> = {};
      for (const sub of subs) {
        const extId = `azure-subscription-${sub.subscription_id}`;
        try {
          const resp = await resourcesApi.listAll('azure_subscription', undefined, 100, 0);
          const match = resp.data?.resources?.find((r: any) => r.external_id === extId);
          if (match) {
            const accessResp = await resourcesApi.getAccessStatus(match.id);
            statusMap[sub.id] = { hasAccess: accessResp.data.has_access === true, resourceId: match.id };
          } else {
            // Also try azure_vm, azure_storage etc.
            statusMap[sub.id] = { hasAccess: false };
          }
        } catch { statusMap[sub.id] = { hasAccess: false }; }
      }
      return statusMap;
    },
    { enabled: subs.length > 0 && !isAdmin(), refetchInterval: 30000 }
  );

  // Role mappings
  const { data: roleMappingsData } = useQuery('azure-role-mappings', () => azureApi.listRoleMappings());
  const roleMappings = roleMappingsData?.data?.mappings || [];

  const handleRequestAccess = (sub: any) => {
    setPendingSub(sub);
    setAzureAccessDescription('');
    setShowDescriptionModal(true);
  };

  const openAccessRequestForm = async (sub: any) => {
    // Try to find a requestable resource for this subscription
    const extId = `azure-subscription-${sub.subscription_id}`;
    try {
      const resp = await resourcesApi.listAll(undefined, undefined, 200, 0);
      const match = resp.data?.resources?.find((r: Resource) =>
        r.external_id === extId ||
        (r.type?.startsWith('azure_') && r.metadata?.azure_subscription_internal_id === sub.id)
      );
      if (match) {
        setSelectedResourceForRequest(match);
        setShowAccessRequestForm(true);
      } else toast.error('Azure resource not found. Admin needs to run discovery or add resources.');
    } catch (err) { toast.error(errorMessage(err, 'Failed to look up resource.')); }
  };

  const userHasAccess = (sub: any): boolean => {
    if (isAdmin()) return true;
    return accessChecks?.[sub.id]?.hasAccess === true;
  };

  const portalUrl = (sub: any) => `https://portal.azure.com/#@${sub.tenant_id}/resource/subscriptions/${sub.subscription_id}/overview`;

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <PageHeader
        className="mb-6"
        breadcrumbs={
          <Breadcrumbs
            items={[{ label: 'Cloud Providers', href: '/cloud-providers' }, { label: 'Microsoft Azure' }]}
            renderLink={(item) => <Link to={item.href!} className="rc-breadcrumbs__link">{item.label}</Link>}
          />
        }
        icon={<Building2 />}
        title="Microsoft Azure"
        description={`Access Azure resources through ${BRAND.name}`}
        secondaryActions={isAdmin() ? (
          <Button variant="secondary" aria-pressed={showRoleMappings} onClick={() => setShowRoleMappings(!showRoleMappings)}
            leadingIcon={<Shield className="h-4 w-4" />}
            className={clsx(showRoleMappings && 'ring-2 ring-[var(--ds-color-ring)]')}>
            Azure Roles
          </Button>
        ) : undefined}
        primaryAction={isAdmin() ? (
          <Button onClick={() => setShowSubForm(true)} leadingIcon={<Plus className="h-4 w-4" />}>
            Add Subscription
          </Button>
        ) : undefined}
      />

      {/* Subscription Cards */}
      {subsLoading ? (
        <CardGridSkeleton label="Loading Azure subscriptions" />
      ) : listError ? (
        <Card>
          <EmptyState
            icon={<Building2 />}
            title="Could not load Azure subscriptions"
            description="Something went wrong while fetching the list. Check your connection and try again."
            action={<Button variant="secondary" onClick={() => refetchList()} leadingIcon={<RefreshCw className="h-4 w-4" />}>Retry</Button>}
          />
        </Card>
      ) : subs.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Building2 />}
            title="No Azure Subscriptions Configured"
            description={`Add an Azure subscription to enable resource access through ${BRAND.name}.`}
            action={isAdmin() ? (
              <Button onClick={() => setShowSubForm(true)} leadingIcon={<Plus className="h-4 w-4" />}>
                Add Subscription
              </Button>
            ) : undefined}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {subs.map((sub: any) => {
            const hasAccess = userHasAccess(sub);
            return (
              <Card key={sub.id} className="group">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-5">
                    <div className="flex items-center gap-3 min-w-0">
                      <ProviderMark label="AZ" />
                      <div className="min-w-0">
                        <h3 className="text-base font-medium text-[var(--d2d-text-primary)]">{sub.subscription_name}</h3>
                        <p className="text-xs text-[var(--d2d-text-muted)]"><MonoCell value={String(sub.subscription_id ?? '')} /></p>
                      </div>
                    </div>
                    {isAdmin() && (
                      <IconButton aria-label="Remove subscription" tooltip="Remove subscription" size="sm" icon={<Trash2 className="h-4 w-4" />}
                        onClick={async () => {
                          if (await confirm({
                            title: 'Remove this subscription?',
                            message: `${sub.subscription_name} (${sub.subscription_id}) will be removed from ${BRAND.name}.`,
                            confirmLabel: 'Remove subscription',
                            variant: 'danger',
                          })) deleteSubMutation.mutate(sub.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 hover:text-[var(--danger-text)]" />
                    )}
                  </div>

                  {/* Status */}
                  <div className="flex items-center gap-2 mb-5 px-3 py-2 rounded-lg bg-[var(--d2d-bg-secondary)]">
                    {hasAccess ? (
                      <StatusBadge value="approved">Access Granted</StatusBadge>
                    ) : (
                      <OutcomeBadge value="required" tone="new">Access Required</OutcomeBadge>
                    )}
                    {sub.tenant_id && (
                      <span className="ml-auto min-w-0 text-micro text-[var(--d2d-text-muted)]">
                        <MonoCell value={sub.tenant_id} width="8rem" />
                      </span>
                    )}
                  </div>

                  {/* Admin: Open Portal */}
                  {hasAccess && isAdmin() && (
                    <Button as="a" href={portalUrl(sub)} target="_blank" rel="noopener noreferrer" className="w-full"
                      leadingIcon={<ExternalLink className="h-4 w-4" />}>
                      Open Azure Portal
                    </Button>
                  )}

                  {/* Non-admin with access */}
                  {hasAccess && !isAdmin() && (
                    <div className="space-y-2">
                      <Button as="a" href={portalUrl(sub)} target="_blank" rel="noopener noreferrer" className="w-full"
                        leadingIcon={<ExternalLink className="h-4 w-4" />}>
                        Open Azure Portal
                      </Button>
                      <Button variant="ghost" size="sm" className="w-full" onClick={() => handleRequestAccess(sub)}
                        leadingIcon={<Plus className="h-3.5 w-3.5" />}>
                        Request More Access
                      </Button>
                    </div>
                  )}

                  {/* No access — request button */}
                  {!hasAccess && (
                    <Button variant="secondary" className="w-full" onClick={() => handleRequestAccess(sub)}
                      leadingIcon={<Clock className="h-4 w-4" />}>
                      Request Access
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Role Mappings Panel (admin only) */}
      {showRoleMappings && isAdmin() && (
        <Card className="mt-8">
          <Card.Header
            title="Available Azure Roles"
            description="Azure RBAC roles that users can request access to"
            actions={
              <Button onClick={() => setShowAddMapping(true)} leadingIcon={<Plus className="h-4 w-4" />}>
                Add Role
              </Button>
            }
          />
          <div className="divide-y divide-[var(--d2d-border)]">
            {roleMappings.length === 0 ? (
              <div className="px-6 py-8 text-center text-sm text-[var(--d2d-text-muted)]">
                No Azure roles configured. Add roles that users can request access to.
              </div>
            ) : (
              roleMappings.map((m: any) => (
                <div key={m.id} className="px-6 py-4 flex items-center justify-between hover:bg-[var(--ds-color-bg-hover)]">
                  <div className="flex items-center gap-3 min-w-0">
                    <Shield aria-hidden className="h-4 w-4 flex-none text-[var(--d2d-text-muted)]" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[var(--d2d-text-primary)]">{m.azure_role_name}</div>
                      {m.scope && <div className="text-xs text-[var(--d2d-text-secondary)] mt-0.5"><MonoCell value={m.scope} /></div>}
                    </div>
                  </div>
                  <IconButton aria-label="Remove role" tooltip="Remove role" size="sm" icon={<Trash2 className="h-4 w-4" />}
                    className="hover:text-[var(--danger-text)]"
                    onClick={async () => {
                      if (await confirm({
                        title: 'Remove this role?',
                        message: `${m.azure_role_name} will no longer be available to request.`,
                        confirmLabel: 'Remove role',
                        variant: 'danger',
                      })) azureApi.deleteRoleMapping(m.id)
                        .then(() => { queryClient.invalidateQueries('azure-role-mappings'); toast.success('Removed'); })
                        .catch((err) => { toast.error(errorMessage(err, 'Failed to remove role')); });
                    }} />
                </div>
              ))
            )}
          </div>
        </Card>
      )}

      {/* Info Banner */}
      <Banner variant="info" title="How Azure Access Works" className="mt-8">
        {isAdmin()
          ? 'As an admin, you can access the Azure Portal directly. Use Azure Roles to register RBAC roles (e.g., Reader, Contributor, Storage Blob Data Reader) that users can request access to.'
          : 'Click "Request Access" to submit an access request. Once approved by an admin, you can open the Azure Portal with the assigned role permissions.'}
      </Banner>

      {/* Modals */}
      {showSubForm && (
        <AddSubscriptionModal onClose={() => setShowSubForm(false)}
          onSubmit={(data) => createSubMutation.mutate(data)} isLoading={createSubMutation.isLoading} />
      )}

      {showAddMapping && subs.length > 0 && (
        <AddAzureRoleMappingModal subscriptions={subs} onClose={() => setShowAddMapping(false)}
          onSuccess={() => { queryClient.invalidateQueries('azure-role-mappings'); setShowAddMapping(false); }} />
      )}

      {showDescriptionModal && pendingSub && (
        <AzureAccessDescriptionModal
          subName={pendingSub.subscription_name}
          onSubmit={async (description: string) => {
            setShowDescriptionModal(false);
            setAzureAccessDescription(description);
            await openAccessRequestForm(pendingSub);
            setPendingSub(null);
          }}
          onClose={() => { setShowDescriptionModal(false); setPendingSub(null); }}
        />
      )}

      {showAccessRequestForm && selectedResourceForRequest && (
        <AccessRequestForm resource={selectedResourceForRequest}
          metadata={azureAccessDescription ? { azure_access_description: azureAccessDescription } : undefined}
          onClose={() => { setShowAccessRequestForm(false); setSelectedResourceForRequest(null); setAzureAccessDescription(''); }}
          onSuccess={() => {
            queryClient.invalidateQueries('azure-sub-access');
            setShowAccessRequestForm(false); setSelectedResourceForRequest(null); setAzureAccessDescription('');
            toast.success('Access request submitted!');
          }}
        />
      )}
    </div>
  );
}

// ===== Add Subscription Modal =====
function AddSubscriptionModal({ onClose, onSubmit, isLoading }: { onClose: () => void; onSubmit: (d: any) => void; isLoading: boolean }) {
  const formId = useId();
  const [subId, setSubId] = useState('');
  const [subName, setSubName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  return (
    <Modal open onClose={onClose} title="Add Azure Subscription" size="md"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} disabled={isLoading}>{isLoading ? 'Adding...' : 'Add Subscription'}</Button>
        </>
      }>
      <form id={formId} onSubmit={(e) => { e.preventDefault(); onSubmit({ subscription_id: subId, subscription_name: subName, tenant_id: tenantId, client_id: clientId, client_secret: clientSecret }); }} className="space-y-4">
        <Input label="Subscription ID" className="[&_input]:font-mono" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={subId} onChange={(e) => setSubId(e.target.value)} required />
        <Input label="Subscription Name" placeholder="Production" value={subName} onChange={(e) => setSubName(e.target.value)} required />
        <Input label="Tenant ID" className="[&_input]:font-mono" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={tenantId} onChange={(e) => setTenantId(e.target.value)} required />
        <Input label="Client ID (App Registration)" className="[&_input]:font-mono" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={clientId} onChange={(e) => setClientId(e.target.value)} required />
        <Input label="Client Secret" className="[&_input]:font-mono" type="password" placeholder="••••••••" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} required />
      </form>
    </Modal>
  );
}

// ===== Azure Access Description Modal =====
function AzureAccessDescriptionModal({ subName, onSubmit, onClose }: { subName: string; onSubmit: (desc: string) => void; onClose: () => void }) {
  const [description, setDescription] = useState('');
  return (
    <Modal open onClose={onClose} title="Request Azure Access" size="md"
      description={<>Describe the access you need for <span className="font-medium">{subName}</span></>}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => { if (description.trim()) onSubmit(description.trim()); }}
            disabled={!description.trim()}
          >Continue</Button>
        </>
      }>
      <div className="space-y-4">
        <Textarea label="What Azure access do you need?" rows={4} resize="none"
          placeholder="e.g., I need Reader access to storage accounts for monitoring, or Contributor access to VMs for deployment..."
          value={description} onChange={(e) => setDescription(e.target.value)} autoFocus
          help="The admin will review your request and assign the appropriate Azure role." />
        <Banner variant="info">
          Be specific about the services and resources you need (e.g., "Reader access to storage accounts" or "Contributor access to resource group X").
        </Banner>
      </div>
    </Modal>
  );
}

// ===== Add Azure Role Mapping Modal =====
function AddAzureRoleMappingModal({ subscriptions, onClose, onSuccess }: { subscriptions: any[]; onClose: () => void; onSuccess: () => void }) {
  const formId = useId();
  const [subId, setSubId] = useState(subscriptions[0]?.id || '');
  const [roleName, setRoleName] = useState('');
  const [scope, setScope] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await azureApi.createRoleMapping({ azure_subscription_id: subId, azure_role_name: roleName, scope: scope || undefined });
      toast.success('Azure role created');
      onSuccess();
    } catch (err) { toast.error(errorMessage(err, 'Failed to create role')); }
    setIsLoading(false);
  };

  return (
    <Modal open onClose={onClose} title="Add Azure Role" size="md"
      description="Register an Azure RBAC role that users can request access to"
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" form={formId} disabled={isLoading}>{isLoading ? 'Adding...' : 'Add Role'}</Button>
        </>
      }>
      <form id={formId} onSubmit={handleSubmit} className="space-y-4">
        <Select label="Azure Subscription" value={subId} onChange={(e) => setSubId(e.target.value)}>
          {subscriptions.map((s: any) => <option key={s.id} value={s.id}>{s.subscription_name} ({s.subscription_id})</option>)}
        </Select>
        <Input label="Azure RBAC Role Name" placeholder="e.g., Reader, Contributor, Storage Blob Data Reader" value={roleName}
          onChange={(e) => setRoleName(e.target.value)} required help="The Azure built-in or custom role name to assign" />
        <Input label="Scope (optional)" className="[&_input]:font-mono" placeholder="/subscriptions/{id}/resourceGroups/{rg}" value={scope}
          onChange={(e) => setScope(e.target.value)} help="Leave empty for subscription-level scope" />
      </form>
    </Modal>
  );
}
